import { useState } from "react";
import { supabase } from "../lib/supabase";
import { C } from "../constants/colors";

const EXH_TYPES = ['Solo show','Group show','Open prize','Residency program','Art fair','Other'];
const BLANK_EXH = { type:'Group show', title:'', artists:'', location:'', year:'' };

export function ExhibitionHistorySection({ profile, onHistorySaved }) {
  const [entries, setEntries] = useState(() => {
    try { return JSON.parse(profile?.exhibition_history || '[]'); } catch { return []; }
  });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...BLANK_EXH });
  const [saving, setSaving] = useState(false);

  const openNew = () => { setForm({ ...BLANK_EXH }); setEditing('new'); };
  const openEdit = (i) => { setForm({ ...entries[i] }); setEditing(i); };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const updated = editing === 'new'
      ? [form, ...entries]
      : entries.map((e,i) => i === editing ? form : e);
    const { error } = await supabase.from('profiles').update({ exhibition_history: JSON.stringify(updated) }).eq('id', profile.id);
    setSaving(false);
    if (error) return;
    setEntries(updated);
    onHistorySaved(JSON.stringify(updated));
    setEditing(null);
  };

  const handleDelete = async (i) => {
    const updated = entries.filter((_,idx) => idx !== i);
    await supabase.from('profiles').update({ exhibition_history: JSON.stringify(updated) }).eq('id', profile.id);
    setEntries(updated);
    onHistorySaved(JSON.stringify(updated));
  };

  const inp = { background:C.parch, border:`1px solid ${C.sand}`, borderRadius:6, padding:"8px 10px", fontSize:".82rem", color:C.bark, width:"100%", boxSizing:"border-box", outline:"none", fontFamily:"inherit" };
  const lbl = { fontSize:".6rem", fontFamily:"'DM Mono',monospace", letterSpacing:"1px", textTransform:"uppercase", color:C.muted, display:"block", marginBottom:3 };

  return (
    <div>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12 }}>
        <span className="slbl" style={{ margin:0 }}>Exhibition History</span>
        <button className="btn-p" style={{ fontSize:".64rem",padding:"7px 14px",minHeight:36 }} onClick={openNew}>+ Add exhibition</button>
      </div>

      {editing !== null && (
        <div style={{ background:C.parchD,border:`1px solid ${C.sand}`,borderRadius:8,padding:"16px",marginBottom:14 }}>
          <div style={{ marginBottom:10 }}>
            <label style={lbl}>Type</label>
            <select style={inp} value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))}>
              {EXH_TYPES.map(t=><option key={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ marginBottom:10 }}>
            <label style={lbl}>Exhibition title *</label>
            <input style={inp} value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="Title of the exhibition"/>
          </div>
          <div style={{ display:"flex",gap:8,marginBottom:10 }}>
            <div style={{ flex:2 }}>
              <label style={lbl}>Location</label>
              <input style={inp} value={form.location} onChange={e=>setForm(f=>({...f,location:e.target.value}))} placeholder="Venue, City"/>
            </div>
            <div style={{ flex:1 }}>
              <label style={lbl}>Year</label>
              <input style={inp} value={form.year} onChange={e=>setForm(f=>({...f,year:e.target.value}))} placeholder="2022"/>
            </div>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={lbl}>Featured artists</label>
            <input style={inp} value={form.artists} onChange={e=>setForm(f=>({...f,artists:e.target.value}))} placeholder="Names of participating artists"/>
          </div>
          <div style={{ display:"flex",gap:8 }}>
            <button className="btn-dk" style={{ flex:1,padding:"9px" }} onClick={()=>setEditing(null)} disabled={saving}>Cancel</button>
            <button className="btn-p" style={{ flex:2,padding:"9px" }} onClick={handleSave} disabled={saving||!form.title.trim()}>{saving?"Saving…":"Save"}</button>
          </div>
        </div>
      )}

      {entries.length === 0 && editing === null && (
        <div style={{ border:`2px dashed ${C.sand}`,borderRadius:8,padding:"28px 16px",textAlign:"center" }}>
          <div style={{ fontFamily:"'Fraunces',serif",fontSize:".95rem",color:C.bark,marginBottom:6 }}>No exhibitions yet</div>
          <p style={{ fontSize:".78rem",color:C.muted,marginBottom:0,lineHeight:1.65 }}>Add your gallery's exhibition history — including shows before joining opencall.</p>
        </div>
      )}

      {entries.map((entry, idx) => (
        <div key={idx} style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"12px 14px",background:C.parch,border:`1px solid ${C.sand}`,borderRadius:6,marginBottom:8 }}>
          <div>
            <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:3 }}>
              <span style={{ fontFamily:"'DM Mono',monospace",fontSize:".56rem",letterSpacing:"1px",textTransform:"uppercase",color:C.muted }}>{entry.type}</span>
              {entry.year && <span style={{ fontFamily:"'DM Mono',monospace",fontSize:".6rem",color:C.clay }}>{entry.year}</span>}
            </div>
            <div style={{ fontSize:".85rem",color:C.bark,fontWeight:500,marginBottom:2 }}>{entry.title}</div>
            {entry.location && <div style={{ fontSize:".72rem",color:C.muted }}>{entry.location}</div>}
            {entry.artists && <div style={{ fontSize:".72rem",color:C.muted,marginTop:2,fontStyle:"italic" }}>{entry.artists}</div>}
          </div>
          <div style={{ display:"flex",gap:6,flexShrink:0,marginLeft:10 }}>
            <button onClick={()=>openEdit(idx)} style={{ background:"none",border:`1px solid ${C.sand}`,borderRadius:4,padding:"3px 9px",fontSize:".68rem",color:C.muted,cursor:"pointer" }}>Edit</button>
            <button onClick={()=>handleDelete(idx)} style={{ background:"none",border:`1px solid ${C.sand}`,borderRadius:4,padding:"3px 9px",fontSize:".68rem",color:C.muted,cursor:"pointer" }}>✕</button>
          </div>
        </div>
      ))}
    </div>
  );
}
